import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { Editor } from '@tinymce/tinymce-react';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

import { sanitizedDescription } from '../utils/helpers';
import { DESCRIPTION_LIMIT } from '../utils/constants';
import Text from '../../../shared/Text';

const Description = ({ value, onChange, onInitialValue, error }) => {
  const { t } = useTranslation('tileForm');
  const theme = useTheme();
  const [length, setLength] = useState(0);
  const [editorKey, setEditorKey] = useState(0);
  const [description, setDescription] = useState(value);
  const getDescriptionLength = desc => sanitizedDescription(desc).length;

  const debouncedOnChange = useCallback(
    debounce(newValue => {
      onChange({ target: { name: 'supportingContent', value: newValue } });
    }, 500),
    [],
  );

  const handleUpdate = (newValue, editor) => {
    const editorLength = getDescriptionLength(editor.getContent());

    setDescription(newValue);
    debouncedOnChange(newValue);
    setLength(editorLength);
  };

  // Handles the resize event manually here and forces re-render
  // by triggering a state update
  useEffect(() => {
    const debouncedResize = debounce(() => {
      setEditorKey(prev => prev + 1);
    }, 300);

    window.addEventListener('resize', debouncedResize);
    return () => window.removeEventListener('resize', debouncedResize);
  }, []);

  return (
    <Wrapper error={!!error}>
      <Editor
        key={editorKey}
        tinymceScriptSrc={import.meta.env.VITE_TINY_MCE_SRC}
        onInit={(evt, editor) => {
          setLength(getDescriptionLength(editor.getContent()));
          onInitialValue(editor.getContent());
        }}
        value={description}
        onEditorChange={handleUpdate}
        init={{
          license_key: 'gpl',
          placeholder: 'Topic Description',
          menubar: false,
          plugins: ['link', 'lists', 'autolink', 'autoresize'],
          link_target_list: false,
          link_title: false,
          link_context_toolbar: true,
          link_assume_external_targets: 'https',
          convert_urls: false,
          toolbar: 'bold italic underline | bullist numlist | link',
          content_style: `body { color: ${theme.colors.black['100']}; font-family:Montserrat,Arial,sans-serif; font-size:16px; margin: 0; }`,
          link_default_target: '_blank',
        }}
      />
      <InputFooter error={!!error}>
        {error && (
          <Text variant="button" color="error.default">
            {error}
          </Text>
        )}
        <Text variant="small4" color={error ? 'error.default' : 'black.60'}>
          {DESCRIPTION_LIMIT - length} {t('charactersLeft')}
        </Text>
      </InputFooter>
    </Wrapper>
  );
};

export default Description;

Description.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func,
  onInitialValue: PropTypes.func,
};

const Wrapper = styled.div`
  margin: 0 0 16px 0;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: ${props => props.error && '2px solid red'};

  .tox-tinymce {
    border: none;

    .tox-edit-area::before {
      border: none;
    }

    .tox-toolbar__primary {
      background: none;
      border-bottom: solid 1px;
      border-color: ${themeGet('colors.black.10')};

      .tox-toolbar__group {
        border-color: ${themeGet('colors.black.10')} !important;
      }
    }

    .tox-statusbar {
      display: none;
    }
  }
`;

const InputFooter = styled.div`
  display: flex;
  justify-content: ${props => (props.error ? 'space-between' : 'flex-end')};
  margin: 0 0 8px;
`;
